import React, { useEffect, useRef, useState } from "react";
import { Formik, Form } from "formik";
import { epliValidationSchema } from "../../utils/ValidationSchema";
import { EPLIFormType } from "../../utils/formType";
import { EPLIInitialFormValues } from "../../utils/initialFormValues";
import EPLIComponent from "./components/EPLIComponent";
import { EPLIType } from "types";
import { useUpdateEpliMutation } from "types/generated/operations";
import { getProspectUuid } from "utils/idHelpers";
import { convertStringIntoBool, errorMessageMap } from "utils/helper";
import FooterButton from "uicomponents/FooterButton";
import Alert from "uicomponents/Alert";
import {
  BACK_BUTTON_TEXT,
  NEXT_BUTTON_TEXT,
  LOADING_BUTTON_TEXT,
} from "pages/WorkersCompApplcation/utils/contants";
import {
  trackFormError,
  trackPageViewUserStep,
} from "utils/analytics/trackEvent";

interface IOwnersProps {
  setNextStep: () => void;
  setPreviousStep: () => void;
  data: EPLIType;
}

const EPLI = ({ ...props }: IOwnersProps): JSX.Element => {
  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageViewUserStep(9, "epli");
  }, []);
  const { setNextStep, setPreviousStep, data } = props;

  const initialFormValues: EPLIFormType = EPLIInitialFormValues(data);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoadingSteps, setIsLoadingSteps] = useState(false);

  const [updateEPLI, { loading: updateEPLILoading }] = useUpdateEpliMutation();

  const handleOnSubmit = async (values: EPLIFormType) => {
    setIsLoadingSteps(true);
    try {
      await updateEPLI({
        variables: {
          uuid: getProspectUuid(),
          hasPolicy: convertStringIntoBool(values.hasPolicy),
          receivedEpliAllegations: convertStringIntoBool(
            values.receivedEpliAllegations
          ),
          beenSuedFormerEmp: convertStringIntoBool(values.beenSuedFormerEmp),
          claimDetails: values.claimDetails,
          overTwentyFiveEe: convertStringIntoBool(values.overTwentyFiveEe),
          offeredSeverancePackages: convertStringIntoBool(
            values.offeredSeverancePackages
          ),
          noEpliLossRunsReportOtherReason:
            values.noEpliLossRunsReportOtherReason,
          noEpliLossRunsReportReason: values.noEpliLossRunsReportReason,
        },
      });
      setNextStep();
    } catch (error: any) {
      const errorMessage: string = error?.message;
      errorMessageMap[errorMessage] &&
        setErrorMessage(errorMessageMap[errorMessage]);
      trackFormError(error, errorMessage);
      setIsError(true);
      return;
    }
  };

  const handleBack = (): void => {
    setPreviousStep();
  };

  const fieldRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

  // Helper function that makes the browser scroll a specific field into view
  const focusField = (fieldName: string) => {
    if (fieldRefs.current[fieldName]) {
      fieldRefs.current[fieldName]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };

  return (
    <>
      <Formik
        initialValues={initialFormValues}
        validationSchema={epliValidationSchema}
        onSubmit={handleOnSubmit}
      >
        {({ errors, handleSubmit }) => (
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              const firstErrorFieldName = Object.keys(errors)[0];
              focusField(firstErrorFieldName);
              handleSubmit(e);
            }}
          >
            <EPLIComponent
              data={props.data?.applications?.[0]}
              fieldRefs={fieldRefs}
            />
            <FooterButton
              nextButtonText={
                updateEPLILoading || isLoadingSteps
                  ? LOADING_BUTTON_TEXT
                  : NEXT_BUTTON_TEXT
              }
              backButtonText={BACK_BUTTON_TEXT}
              disabled={updateEPLILoading || isLoadingSteps}
              handleBackClick={handleBack}
            />
            {isError && <Alert message={errorMessage} />}
          </Form>
        )}
      </Formik>
    </>
  );
};

export default EPLI;
